import { createBrowserRouter, Link, Navigate, type RouteObject } from "react-router-dom";
import { TenantLayout } from "./components/TenantLayout";
import { SelectPage } from "./pages/SelectPage";
import { VisualizePage } from "./pages/VisualizePage";
import { SchedulePage } from "./pages/SchedulePage";
import { MobileUploadPage } from "./pages/MobileUploadPage";
import { PostcardLandingPage } from "./pages/PostcardLandingPage";
import { DEMO_SLUG, SHOWROOM_SEGMENT, getSubdomainTenant } from "./lib/tenant";

// The themed catalogue screens, shared by every scope. Mounted at root on a tenant subdomain
// (accuratedoor.renoglance.com/visualize) and at /:tenantSlug, /renoglance (the all-manufacturers
// demo — matched by the :tenantSlug param and resolved server-side), or /manufacturer/:slug
// otherwise (local dev + path access). "c/:code" is the postcard QR landing — being a scope child
// registers it in BOTH router modes (subdomain /c/{code}, path /{tenantSlug}/c/{code}) ahead of
// the catch-all redirects below.
const scopeChildren: RouteObject[] = [
  { index: true, element: <SelectPage /> },
  { path: "visualize", element: <VisualizePage /> },
  { path: "schedule", element: <SchedulePage /> },
  { path: "c/:code", element: <PostcardLandingPage /> },
];

const onSubdomain = getSubdomainTenant() !== null;

export const router = createBrowserRouter(
  onSubdomain
    ? [
        { path: "/", element: <TenantLayout />, children: scopeChildren },
        // Full active-catalogue "showroom" mode — same dealer shell, price-free browse of everything they've enabled.
        { path: `/${SHOWROOM_SEGMENT}`, element: <TenantLayout />, children: scopeChildren },
        // Standalone phone-handoff flow — no shell, opened on a second device.
        { path: "/m/:pairingId", element: <MobileUploadPage /> },
        { path: "*", element: <Navigate to="/" replace /> },
      ]
    : [
        // Manufacturer catalogue mode — registered before /:tenantSlug so "manufacturer" is never
        // read as a dealer slug (it is also a reserved tenant slug server-side).
        { path: "/manufacturer/:manufacturerSlug", element: <TenantLayout />, children: scopeChildren },
        { path: "/manufacturer/:manufacturerSlug/m/:pairingId", element: <MobileUploadPage /> },
        // Dealer showroom (full active catalogue) — registered before /:tenantSlug so "showroom" is read as
        // the mode segment, not a dealer slug. QR pairing stays mode-agnostic (/{slug}/m/:pairingId).
        { path: `/:tenantSlug/${SHOWROOM_SEGMENT}`, element: <TenantLayout />, children: scopeChildren },
        { path: "/:tenantSlug", element: <TenantLayout />, children: scopeChildren },
        { path: "/:tenantSlug/m/:pairingId", element: <MobileUploadPage /> },
        { path: "/", element: <Landing /> },
        { path: "*", element: <Navigate to="/" replace /> },
      ],
);

function Landing() {
  return (
    <div style={{ fontFamily: "system-ui, -apple-system, sans-serif", padding: 40, textAlign: "center" }}>
      <h1>RenoGlance Visualizer</h1>
      <p style={{ color: "#666" }}>
        Open your dealer's link to start (e.g. <code>/your-dealer</code>).
      </p>
      <p style={{ color: "#666" }}>
        No dealer link? <Link to={`/${DEMO_SLUG}`}>Browse the full catalogue demo</Link>.
      </p>
    </div>
  );
}
